import type { MatchBuckets, MonthDetailResponse, OverrideBucketItem, QboTxn, StatementLine } from '@/services/bankRecon'
import { parseBankReconMoney } from './bankReconFormat'
import type { BankReconBucket, BankReconRow } from './bankReconRows'

export const LINKER_PANE_BUCKETS: BankReconBucket[] = [
  'statement_only',
  'qbo_only_in_window',
  'qbo_stale',
  'qbo_outstanding',
]

export type LinkedMatchGroup = {
  linkGroupId?: string | null
  lines: Array<{
    lineKey: string
    line?: StatementLine | null
  }>
  items: Array<{
    overrideId: number
    txn?: QboTxn | null
    note: string
  }>
}

export type LinkerQboRow = QboTxn & {
  ignored: boolean
  ignoreOverrideId?: number
  linked?: boolean
  matchLineKey?: string
  linkGroupId?: string
  linkOverrideIds?: number[]
}

export type LinkerStatementLine = StatementLine & {
  linked?: boolean
}

/** QBO-only linker table: selected month through month-end + 4 calendar days. */
const LINKER_DISPLAY_GRACE_DAYS = 4

function reportMonthLastDay(reportMonth: string): string {
  const [yearText, monthText] = reportMonth.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const lastDay = new Date(year, month, 0).getDate()
  return `${reportMonth}-${String(lastDay).padStart(2, '0')}`
}

function reportMonthLinkerDisplayEndDate(reportMonth: string): string {
  const [yearText, monthText] = reportMonth.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const lastDay = new Date(year, month, 0).getDate()
  const endDate = new Date(year, month - 1, lastDay + LINKER_DISPLAY_GRACE_DAYS)
  const endYear = endDate.getFullYear()
  const endMonth = String(endDate.getMonth() + 1).padStart(2, '0')
  const endDay = String(endDate.getDate()).padStart(2, '0')
  return `${endYear}-${endMonth}-${endDay}`
}

export function isTxnInLinkerDisplayWindow(txnDate: string, reportMonth: string): boolean {
  const start = `${reportMonth}-01`
  const end = reportMonthLinkerDisplayEndDate(reportMonth)
  return txnDate >= start && txnDate <= end
}

function isTxnInLinkerGracePeriod(txnDate: string, reportMonth: string): boolean {
  const monthEnd = reportMonthLastDay(reportMonth)
  const end = reportMonthLinkerDisplayEndDate(reportMonth)
  return txnDate > monthEnd && txnDate <= end
}

function isQboReconciled(txn: QboTxn): boolean {
  return txn.cleared_status === 'R'
}

function linkerStatementEndDate(detail: MonthDetailResponse): string {
  if (detail.statement?.statement_end) return detail.statement.statement_end
  const [yearText, monthText] = detail.report_month.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const lastDay = new Date(year, month, 0).getDate()
  return `${detail.report_month}-${String(lastDay).padStart(2, '0')}`
}

function qboTxnAgeDays(txnDate: string, statementEnd: string): number {
  const txnMs = Date.parse(txnDate)
  const endMs = Date.parse(statementEnd)
  if (!Number.isFinite(txnMs) || !Number.isFinite(endMs)) return 0
  return Math.floor((endMs - txnMs) / (1000 * 60 * 60 * 24))
}

const DEFAULT_STALE_DAYS = 60

export function sumSignedAmounts(amounts: Array<string | null | undefined>): number {
  return amounts.reduce((total, amount) => total + parseBankReconMoney(amount), 0)
}

export function roundBankReconCents(value: number): number {
  const rounded = Math.round(value * 100) / 100
  return rounded === 0 ? 0 : rounded
}

export function matchCoveringPairs(lineKeys: string[], txnIds: string[]): Array<[string, string]> {
  if (lineKeys.length === 0 || txnIds.length === 0) return []
  const pairs: Array<[string, string]> = []
  const count = Math.max(lineKeys.length, txnIds.length)
  for (let index = 0; index < count; index += 1) {
    pairs.push([
      lineKeys[index % lineKeys.length]!,
      txnIds[index % txnIds.length]!,
    ])
  }
  return pairs
}

export function computeLinkerDifference(
  statementAmounts: Array<string | null | undefined> | string | null | undefined,
  qboAmounts: Array<string | null | undefined>,
): number {
  const stmtAmounts = Array.isArray(statementAmounts) ? statementAmounts : [statementAmounts]
  const raw = sumSignedAmounts(stmtAmounts) - sumSignedAmounts(qboAmounts)
  return roundBankReconCents(raw)
}

export function collectIgnoredQboTxnMap(
  detail: MonthDetailResponse,
): Map<string, { overrideId: number; txn: QboTxn }> {
  const ignored = new Map<string, { overrideId: number; txn: QboTxn }>()
  for (const item of detail.buckets.overridden) {
    if (item.kind !== 'ignore_qbo_txn' || !item.txn_id || !item.txn) continue
    const overrideId = item.override_id
    if (overrideId == null) continue
    ignored.set(item.txn_id, { overrideId, txn: item.txn })
  }
  return ignored
}

function linkedMatchGroupKey(item: OverrideBucketItem): string | null {
  if (item.kind !== 'match') return null
  if (item.link_group_id) return `gid:${item.link_group_id}`
  if (item.line_key) return `line:${item.line_key}`
  return null
}

function collectLinkedQboTxnMap(
  detail: MonthDetailResponse,
): Map<string, { txn: QboTxn; matchLineKey?: string; linkGroupId?: string; linkOverrideIds: number[] }> {
  const groupsByKey = new Map<
    string,
    { overrideIds: number[]; txns: Map<string, QboTxn>; matchLineKey?: string; linkGroupId?: string }
  >()

  for (const item of detail.buckets.overridden) {
    if (item.kind !== 'match' || !item.txn_id || !item.txn) continue
    const overrideId = item.override_id
    if (overrideId == null) continue
    const groupKey = linkedMatchGroupKey(item)
    if (!groupKey) continue

    let entry = groupsByKey.get(groupKey)
    if (!entry) {
      entry = {
        overrideIds: [],
        txns: new Map(),
        matchLineKey: item.line_key ?? undefined,
        linkGroupId: item.link_group_id ?? undefined,
      }
      groupsByKey.set(groupKey, entry)
    }
    entry.overrideIds.push(overrideId)
    const lineKey = item.txn.qbo_line_key || item.txn_id
    entry.txns.set(lineKey, item.txn)
    if (item.link_group_id) entry.linkGroupId = item.link_group_id
    if (item.line_key && !entry.matchLineKey) entry.matchLineKey = item.line_key
  }

  const linked = new Map<
    string,
    { txn: QboTxn; matchLineKey?: string; linkGroupId?: string; linkOverrideIds: number[] }
  >()
  for (const entry of groupsByKey.values()) {
    const linkOverrideIds = entry.overrideIds
    for (const [lineKey, txn] of entry.txns) {
      linked.set(lineKey, {
        txn,
        matchLineKey: entry.matchLineKey,
        linkGroupId: entry.linkGroupId,
        linkOverrideIds,
      })
    }
  }
  return linked
}

export function overrideIdsForLinkedQboTxn(
  groups: LinkedMatchGroup[],
  qboLineKey: string,
): number[] {
  for (const group of groups) {
    if (!group.items.some((item) => (item.txn?.qbo_line_key || item.txn?.txn_id) === qboLineKey)) {
      continue
    }
    return group.items.map((item) => item.overrideId)
  }
  return []
}

export function collectLinkerQboTxns(
  detail: MonthDetailResponse,
  options?: { includeAllUnreconciled?: boolean },
): LinkerQboRow[] {
  const includeAllUnreconciled = options?.includeAllUnreconciled ?? false
  const reportMonth = detail.report_month
  const buckets = detail.buckets
  const ignoredByTxnId = collectIgnoredQboTxnMap(detail)
  const linkedByLineKey = collectLinkedQboTxnMap(detail)
  const rows: QboTxn[] = [...buckets.qbo_only_in_window, ...buckets.qbo_stale]

  for (const txn of [
    ...buckets.qbo_outstanding,
    ...(buckets.qbo_unreconciled_out_of_window ?? []),
  ]) {
    if (!isTxnInLinkerGracePeriod(txn.txn_date, reportMonth)) continue
    rows.push(txn)
  }

  if (includeAllUnreconciled) {
    for (const txn of [
      ...buckets.qbo_outstanding,
      ...buckets.qbo_stale,
      ...buckets.qbo_unreconciled_out_of_window ?? [],
    ]) {
      if (isTxnInLinkerDisplayWindow(txn.txn_date, reportMonth)) continue
      if (isQboReconciled(txn)) continue
      const lineKey = txn.qbo_line_key || txn.txn_id
      if (linkedByLineKey.has(lineKey)) continue
      rows.push(txn)
    }
  }

  const byLineKey = new Map<string, LinkerQboRow>()
  for (const txn of rows) {
    const inDisplayWindow = isTxnInLinkerDisplayWindow(txn.txn_date, reportMonth)
    if (!inDisplayWindow && !includeAllUnreconciled) continue
    const lineKey = txn.qbo_line_key || txn.txn_id
    byLineKey.set(lineKey, { ...txn, ignored: false })
  }
  for (const [lineKey, entry] of linkedByLineKey) {
    if (!isTxnInLinkerDisplayWindow(entry.txn.txn_date, reportMonth)) continue
    if (ignoredByTxnId.has(entry.txn.txn_id)) continue
    byLineKey.set(lineKey, {
      ...entry.txn,
      ignored: false,
      linked: true,
      matchLineKey: entry.matchLineKey,
      linkGroupId: entry.linkGroupId,
      linkOverrideIds: entry.linkOverrideIds,
    })
  }
  for (const [txnId, entry] of ignoredByTxnId) {
    if (!isTxnInLinkerDisplayWindow(entry.txn.txn_date, reportMonth)) continue
    const lineKey = entry.txn.qbo_line_key || txnId
    byLineKey.set(lineKey, {
      ...entry.txn,
      ignored: true,
      ignoreOverrideId: entry.overrideId,
    })
  }

  return [...byLineKey.values()].sort((left, right) => left.txn_date.localeCompare(right.txn_date))
}

export function collectLinkerStatementLines(detail: MonthDetailResponse): LinkerStatementLine[] {
  const byLineKey = new Map<string, LinkerStatementLine>()

  for (const item of detail.buckets.overridden) {
    if (item.kind !== 'match' || !item.line_key || !item.line) continue
    byLineKey.set(item.line_key, { ...item.line, linked: true })
  }

  for (const line of detail.buckets.statement_only) {
    const existing = byLineKey.get(line.line_key)
    if (existing?.linked) continue
    byLineKey.set(line.line_key, { ...line, linked: false })
  }

  return [...byLineKey.values()].sort((left, right) =>
    left.posted_date.localeCompare(right.posted_date),
  )
}

export function isLinkerPaneBucket(bucket: BankReconBucket): boolean {
  return LINKER_PANE_BUCKETS.includes(bucket)
}

export function isExceptionsTableRow(row: BankReconRow): boolean {
  if (isLinkerPaneBucket(row.bucket)) return false
  if (row.bucket === 'overridden' && row.overrideKind === 'match') return false
  return true
}

export type ExceptionsTableVisibility = 'uncleared' | 'cleared' | 'all'

export function filterExceptionsTableRows(
  rows: BankReconRow[],
  options: { visibility?: ExceptionsTableVisibility } = {},
): BankReconRow[] {
  const visibility = options.visibility ?? 'uncleared'
  return rows.filter((row) => {
    if (!isExceptionsTableRow(row)) return false
    if (row.bucket === 'overridden') {
      return visibility === 'uncleared' || visibility === 'all'
    }
    if (visibility === 'uncleared') return row.bucket === 'matched_uncleared'
    if (visibility === 'cleared') return row.bucket === 'matched_cleared'
    return row.bucket === 'matched_uncleared' || row.bucket === 'matched_cleared'
  })
}

export function linkedMatchGroupReactKey(group: LinkedMatchGroup): string {
  if (group.linkGroupId) return group.linkGroupId
  return group.lines.map((line) => line.lineKey).sort().join('|')
}

export function groupLinkedMatches(detail: MonthDetailResponse): LinkedMatchGroup[] {
  const groups = new Map<string, LinkedMatchGroup>()

  for (const item of detail.buckets.overridden) {
    if (item.kind !== 'match' || !item.line_key || !item.txn_id) continue
    const overrideId = item.override_id
    if (overrideId == null) continue
    const groupKey = linkedMatchGroupKey(item)
    if (!groupKey) continue

    const entry = {
      overrideId,
      txn: item.txn ?? null,
      note: item.note,
    }

    const existing = groups.get(groupKey)
    if (existing) {
      existing.items.push(entry)
      if (!existing.lines.some((line) => line.lineKey === item.line_key)) {
        existing.lines.push({
          lineKey: item.line_key,
          line: item.line ?? null,
        })
      }
      if (item.link_group_id) existing.linkGroupId = item.link_group_id
    } else {
      groups.set(groupKey, {
        linkGroupId: item.link_group_id ?? null,
        lines: [
          {
            lineKey: item.line_key,
            line: item.line ?? null,
          },
        ],
        items: [entry],
      })
    }
  }

  return [...groups.values()].sort((left, right) => {
    const leftDate = left.lines[0]?.line?.posted_date ?? ''
    const rightDate = right.lines[0]?.line?.posted_date ?? ''
    return leftDate.localeCompare(rightDate)
  })
}

export function findStatementLine(
  detail: MonthDetailResponse,
  lineKey: string,
): StatementLine | undefined {
  return detail.buckets.statement_only.find((line) => line.line_key === lineKey)
}

export function findQboTxn(detail: MonthDetailResponse, qboLineKey: string): QboTxn | undefined {
  const buckets = detail.buckets
  const matchLineKey = (txn: QboTxn) => (txn.qbo_line_key || txn.txn_id) === qboLineKey
  return (
    buckets.qbo_only_in_window.find(matchLineKey)
    ?? buckets.qbo_stale.find(matchLineKey)
    ?? buckets.qbo_outstanding.find(matchLineKey)
    ?? buckets.qbo_unreconciled_out_of_window?.find(matchLineKey)
  )
}

function qboLineKeyFor(txn: QboTxn): string {
  return txn.qbo_line_key || txn.txn_id
}

export function isLinkerSelectionReady(
  selectedStatementKeys: Set<string>,
  selectedQboTxnIds: Set<string>,
): boolean {
  return selectedStatementKeys.size > 0 && selectedQboTxnIds.size > 0
}

export function canLinkSelection(
  canEdit: boolean,
  selectedStatementKeys: Set<string>,
  selectedQboTxnIds: Set<string>,
  difference = 0,
): boolean {
  return (
    canEdit
    && isLinkerSelectionReady(selectedStatementKeys, selectedQboTxnIds)
    && difference === 0
  )
}

export function isMatchOverride(item: OverrideBucketItem): boolean {
  return item.kind === 'match' && Boolean(item.line_key) && Boolean(item.txn_id)
}

let optimisticOverrideIdCounter = 0

function nextOptimisticOverrideId(): number {
  optimisticOverrideIdCounter -= 1
  return optimisticOverrideIdCounter
}

function cloneBuckets(buckets: MatchBuckets): MatchBuckets {
  return {
    matched_cleared: [...buckets.matched_cleared],
    matched_uncleared: [...buckets.matched_uncleared],
    statement_only: [...buckets.statement_only],
    qbo_only_in_window: [...buckets.qbo_only_in_window],
    qbo_outstanding: [...buckets.qbo_outstanding],
    qbo_stale: [...buckets.qbo_stale],
    qbo_unreconciled_out_of_window: [...(buckets.qbo_unreconciled_out_of_window ?? [])],
    overridden: [...buckets.overridden],
  }
}

export function computeExceptionCount(buckets: MatchBuckets): number {
  return buckets.statement_only.length
}

function withUpdatedExceptionCount(
  detail: MonthDetailResponse,
  buckets: MatchBuckets,
): MonthDetailResponse {
  return {
    ...detail,
    buckets,
    headlines: {
      ...detail.headlines,
      exception_count: computeExceptionCount(buckets),
    },
  }
}

function removeStatementLine(buckets: MatchBuckets, lineKey: string): StatementLine | undefined {
  const index = buckets.statement_only.findIndex((line) => line.line_key === lineKey)
  if (index >= 0) {
    const [line] = buckets.statement_only.splice(index, 1)
    return line
  }
  const overrideLine = buckets.overridden.find(
    (item) => item.kind === 'match' && item.line_key === lineKey,
  )?.line
  return overrideLine ?? undefined
}

function removeQboTxn(buckets: MatchBuckets, qboLineKey: string): QboTxn | undefined {
  const matchLineKey = (txn: QboTxn) => qboLineKeyFor(txn) === qboLineKey
  const inWindowIndex = buckets.qbo_only_in_window.findIndex(matchLineKey)
  if (inWindowIndex >= 0) {
    const [txn] = buckets.qbo_only_in_window.splice(inWindowIndex, 1)
    return txn
  }
  const staleIndex = buckets.qbo_stale.findIndex(matchLineKey)
  if (staleIndex >= 0) {
    const [txn] = buckets.qbo_stale.splice(staleIndex, 1)
    return txn
  }
  const outstandingIndex = buckets.qbo_outstanding.findIndex(matchLineKey)
  if (outstandingIndex >= 0) {
    const [txn] = buckets.qbo_outstanding.splice(outstandingIndex, 1)
    return txn
  }
  const unreconciledIndex = buckets.qbo_unreconciled_out_of_window.findIndex(matchLineKey)
  if (unreconciledIndex >= 0) {
    const [txn] = buckets.qbo_unreconciled_out_of_window.splice(unreconciledIndex, 1)
    return txn
  }
  const overrideTxn = buckets.overridden.find(
    (item) => item.kind === 'match' && item.txn && qboLineKeyFor(item.txn) === qboLineKey,
  )?.txn
  return overrideTxn ?? undefined
}

function restoreQboTxnBucket(
  buckets: MatchBuckets,
  txn: QboTxn,
  detail: MonthDetailResponse,
  staleDays: number = DEFAULT_STALE_DAYS,
): void {
  const reportMonth = detail.report_month

  if (isTxnInLinkerDisplayWindow(txn.txn_date, reportMonth)) {
    buckets.qbo_only_in_window.push(txn)
    return
  }

  if (txn.cleared_status === 'C') {
    buckets.qbo_unreconciled_out_of_window.push(txn)
    return
  }

  if (txn.cleared_status === 'R') {
    return
  }

  const age = qboTxnAgeDays(txn.txn_date, linkerStatementEndDate(detail))
  if (age > staleDays) {
    buckets.qbo_stale.push(txn)
  } else {
    buckets.qbo_outstanding.push(txn)
  }
}

export function applyLinkOverride(
  detail: MonthDetailResponse,
  payload: {
    statementLineKeys: string[]
    qboTxnIds: string[]
    note: string
  },
): MonthDetailResponse {
  const buckets = cloneBuckets(detail.buckets)
  const lineByKey = new Map<string, StatementLine>()
  for (const lineKey of payload.statementLineKeys) {
    const line = removeStatementLine(buckets, lineKey) ?? findStatementLine(detail, lineKey)
    if (line) lineByKey.set(lineKey, line)
  }
  if (lineByKey.size === 0) return detail

  const pairs = matchCoveringPairs(payload.statementLineKeys, payload.qboTxnIds)
  const newOverrides: OverrideBucketItem[] = []
  for (const [lineKey, qboLineKey] of pairs) {
    const line = lineByKey.get(lineKey)
    if (!line) continue
    const txn = removeQboTxn(buckets, qboLineKey) ?? findQboTxn(detail, qboLineKey)
    if (!txn) continue
    newOverrides.push({
      kind: 'match',
      line_key: lineKey,
      txn_id: txn.txn_id,
      qbo_line_key: qboLineKeyFor(txn),
      note: payload.note,
      override_id: nextOptimisticOverrideId(),
      line,
      txn,
    })
  }

  if (newOverrides.length === 0) return detail

  const pairKeys = new Set(pairs.map(([lineKey, qboLineKey]) => `${lineKey}:${qboLineKey}`))
  buckets.overridden = [
    ...buckets.overridden.filter((item) => {
      if (item.kind !== 'match' || !item.line_key || !item.txn) return true
      const itemLineKey = qboLineKeyFor(item.txn)
      return !pairKeys.has(`${item.line_key}:${itemLineKey}`)
    }),
    ...newOverrides,
  ]

  return withUpdatedExceptionCount(detail, buckets)
}

export function applyUnlinkOverrides(
  detail: MonthDetailResponse,
  overrideIds: number[],
): MonthDetailResponse {
  if (overrideIds.length === 0) return detail

  const overrideIdSet = new Set(overrideIds)
  const buckets = cloneBuckets(detail.buckets)
  const removed = buckets.overridden.filter(
    (item) => item.override_id != null && overrideIdSet.has(item.override_id),
  )
  if (removed.length === 0) return detail

  buckets.overridden = buckets.overridden.filter(
    (item) => item.override_id == null || !overrideIdSet.has(item.override_id),
  )

  const restoredLineKeys = new Set<string>()
  for (const item of removed) {
    if (item.kind !== 'match') continue
    if (item.line_key && item.line && !restoredLineKeys.has(item.line_key)) {
      buckets.statement_only.push(item.line)
      restoredLineKeys.add(item.line_key)
    }
    if (item.txn_id && item.txn) {
      restoreQboTxnBucket(buckets, item.txn, detail)
    }
  }

  buckets.statement_only.sort((left, right) => left.posted_date.localeCompare(right.posted_date))
  buckets.qbo_only_in_window.sort((left, right) => left.txn_date.localeCompare(right.txn_date))
  buckets.qbo_outstanding.sort((left, right) => left.txn_date.localeCompare(right.txn_date))
  buckets.qbo_stale.sort((left, right) => left.txn_date.localeCompare(right.txn_date))
  buckets.qbo_unreconciled_out_of_window.sort((left, right) =>
    left.txn_date.localeCompare(right.txn_date),
  )

  return withUpdatedExceptionCount(detail, buckets)
}

export function uniqueTxnAmountsForLinkedGroup(group: LinkedMatchGroup): Array<string | null | undefined> {
  const seen = new Set<string>()
  const amounts: Array<string | null | undefined> = []
  for (const item of group.items) {
    const lineKey = item.txn ? qboLineKeyFor(item.txn) : null
    if (!lineKey || seen.has(lineKey)) continue
    seen.add(lineKey)
    amounts.push(item.txn?.amount_signed)
  }
  return amounts
}

export function uniqueLineAmountsForLinkedGroup(group: LinkedMatchGroup): Array<string | null | undefined> {
  const seen = new Set<string>()
  const amounts: Array<string | null | undefined> = []
  for (const lineEntry of group.lines) {
    if (seen.has(lineEntry.lineKey)) continue
    seen.add(lineEntry.lineKey)
    amounts.push(lineEntry.line?.amount_signed)
  }
  return amounts
}
